import { useState, useEffect } from "react";
import PagesHeader from "@components/PagesHeader";
import SectionHeader from "../../components/SectionHeader";
import BrandLogo from "../../components/BrandLogo";
import Button from "@components/Button";
import IconCopy from "./img/copy-icon.svg?react";
import CalendarIcon from "./img/calendar-icon.svg";
import UnderlineButton from "@components/UnderlineButton";
import {
  getModuleValue,
  parseCMSBold,
  parseDate,
  parseHTMLToText,
} from "../../Helpers";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PRIZE_CACHE_TIME, PRIZE_STALE_TIME } from "../../api/prize";
import { Navigate, useNavigate, useParams } from "react-router";
import { getCouponDescribe, getCouponServe } from "../../api/coupon";
import NiceModal from "@ebay/nice-modal-react";
import {
  autoPlacement,
  autoUpdate,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import PopupComponent from "../../components/PopupComponent";
import Check from "@assets/check.svg?react";
import { Download } from "lucide-react";
import DOMPurify from "dompurify";

export default function ReceivedCouponPage() {
  const { uuid } = useParams();
  const [selectedTab, setSelectedTab] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const [isPinOpen, setIsPinOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ["coupon-describe", uuid],
    queryFn: () => getCouponDescribe(uuid),
    staleTime: PRIZE_STALE_TIME,
    cacheTime: PRIZE_CACHE_TIME,
  });

  const pin = data?.pin;

  useEffect(() => {
    if (data) {
      const expiresAt = new Date(data.expiresAt);
      const today = new Date();
      const isExpired = expiresAt.getTime() < today.getTime();
      const used = data?.usedAt;

      if (isExpired || used) {
        navigate("/coupons");
      }
    }
  }, [data, history]);

  const tabContent = [
    ...(getModuleValue(
      data?.catalogue?.content?.modules,
      "point_redeem",
      "metadata.about",
    )
      ? [{ copy: "Nasze punkty sprzedaży", ident: "point_redeem" }]
      : []),
    ...(getModuleValue(
      data?.catalogue?.content?.modules,
      "online_redeem",
      "metadata.about",
    )
      ? [{ copy: "Online", ident: "online_redeem" }]
      : []),
    ...(getModuleValue(
      data?.catalogue?.content?.modules,
      "partner_redeem",
      "metadata.about",
    )
      ? [{ copy: "Sklepy partnerskie", ident: "partner_redeem" }]
      : []),
  ];

  const getPointsLogos = getModuleValue(
    data?.catalogue?.content?.modules,
    "online_redeem",
    "metadata.logos",
  );
  const getPartnersLogos = getModuleValue(
    data?.catalogue?.content?.modules,
    "partner_redeem",
    "metadata.logos",
  );

  const onlineRedeem = getModuleValue(
    data?.catalogue?.content?.modules,
    "online_redeem",
    "metadata.onlineStore",
  );

  const onlineRedeemPartner = getModuleValue(
    data?.catalogue?.content?.modules,
    "partner_redeem",
    "metadata.onlineStore",
  );

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      autoPlacement({
        allowedPlacements: ["top-end", "bottom-end"],
      }),
      offset(15),
      shift({ padding: 10 }),
    ],
  });
  const {
    refs: pinRefs,
    floatingStyles: pinFloatingStyles,
    context: pinContext,
  } = useFloating({
    open: isPinOpen,
    onOpenChange: setIsPinOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      autoPlacement({
        allowedPlacements: ["top-end", "bottom-end"],
      }),
      offset(15),
      shift({ padding: 10 }),
    ],
  });

  const role = useRole(context, { role: "tooltip" });
  const pinRole = useRole(pinContext, { role: "tooltip" });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useDismiss(context),
    role,
  ]);

  const {
    getReferenceProps: getPinReferenceProps,
    getFloatingProps: getPinFloatingProps,
  } = useInteractions([useDismiss(pinContext), pinRole]);

  const ident = data?.catalogue?.ident;
  const shouldShowCouponCode =
    ident !== "set_fragrance_2025" && ident !== "vuse_ultra_seeding_box";

  const modules = data?.catalogue?.content?.modules;
  const partnerRedeemExists = modules?.some(
    (module) => module.type === "partner_redeem",
  );
  const redeemModuleType = partnerRedeemExists
    ? "partner_redeem"
    : "online_redeem";

  const isDownloadable = data?.catalogue?.isDownloadable;

  const downloadFile = async () => {
    const { blob, filename } = await getCouponServe(uuid);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const ext = blob.type ? `.${blob.type.split("/")[1]}` : "";
    a.download = filename ?? `${data?.catalogue?.ident ?? "coupon"}${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <section className={"redeem-award-page"}>
      <PagesHeader
        back
        dangerouslySetInnerHTML={parseCMSBold(
          data?.catalogue?.content?.description ?? "",
        )}
      />
      <div className="desktop-wrapper">
        <div className="award-img-desktop">
          <div className="image-hero-wrapper">
            <img
              src={getModuleValue(
                data?.catalogue?.content?.modules,
                "hero_image",
                "file.url",
              )}
              alt={getModuleValue(
                data?.catalogue?.content?.modules,
                "hero_image",
                "metadata.imgDesc",
              )}
            />
            <BrandLogo brands={data?.catalogue?.brands} />
          </div>
        </div>

        <div className="main-content">
          <div className="award-img-mobile">
            <img
              src={getModuleValue(
                data?.catalogue?.content?.modules,
                "thumbnail",
                "file.url",
              )}
              alt={getModuleValue(
                data?.catalogue?.content?.modules,
                "thumbnail",
                "metadata.imgDesc",
              )}
            />
            <BrandLogo brands={data?.catalogue?.brands} />
          </div>

          {data?.catalogue?.codeHidden ? (
            ""
          ) : (
            <div className="award-content">
              <p className="expires">
                <img src={CalendarIcon} alt="CalendarIcon" /> Gültigkeitsdatum:{" "}
                <span>{parseDate(data?.expiresAt)}</span>
              </p>
              <div className="logos-wrapper">
                {(getPointsLogos || getPartnersLogos) && (
                  <div className="logos">
                    {getPointsLogos?.map((e, index) => (
                      <img src={e.file.url} alt={e.alt} key={index} />
                    ))}
                    {getPartnersLogos?.map((e, index) => (
                      <img src={e.file.url} alt={e.alt} key={index} />
                    ))}
                  </div>
                )}
              </div>
              {shouldShowCouponCode && (
                <div className="coupon-code">
                  <p className="code-copy">Gutschein-Code:</p>
                  <PopupComponent
                    getReferenceProps={getReferenceProps}
                    isOpen={isOpen}
                    setIsOpen={setIsOpen}
                    refs={refs}
                    getFloatingProps={getFloatingProps}
                    floatingStyles={floatingStyles}
                  >
                    <div className="copy-tooltip">
                      <Check /> Code kopiert
                    </div>
                  </PopupComponent>
                  <div
                    className="code-flex"
                    ref={refs.setReference}
                    {...getReferenceProps()}
                  >
                    <div
                      onClick={() => {
                        (setIsOpen(true),
                          navigator.clipboard.writeText(`${data?.code}`));
                      }}
                      className="code"
                    >
                      {data?.code}
                      <div className="code-icon">
                        <div className="copy">
                          <IconCopy />
                        </div>
                      </div>
                    </div>
                  </div>
                  {(onlineRedeem || onlineRedeemPartner) && (
                    <div className="buy-online-cta-wrapper">
                      <Button
                        href={
                          onlineRedeemPartner
                            ? onlineRedeemPartner
                            : onlineRedeem
                        }
                      >
                        Besuche den Online-Shop
                      </Button>
                    </div>
                  )}
                </div>
              )}
              {pin && (
                <div className="coupon-code">
                  <p className="code-copy">PIN:</p>
                  <PopupComponent
                    getReferenceProps={getPinReferenceProps}
                    isOpen={isPinOpen}
                    setIsOpen={setIsPinOpen}
                    refs={pinRefs}
                    getFloatingProps={getPinFloatingProps}
                    floatingStyles={pinFloatingStyles}
                  >
                    <div className="copy-tooltip">
                      <Check /> PIN kopiert
                    </div>
                  </PopupComponent>
                  <div
                    className="code-flex"
                    ref={pinRefs.setReference}
                    {...getPinReferenceProps()}
                  >
                    <div
                      onClick={() => {
                        (setIsPinOpen(true),
                          navigator.clipboard.writeText(`${pin}`));
                      }}
                      className="code"
                    >
                      {pin}
                      <div className="code-icon">
                        <div className="copy">
                          <IconCopy />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {data?.hasFile && (
                <div className="coupon-pdf">
                  <SectionHeader>
                    Deine <strong>Datei</strong>
                  </SectionHeader>
                  <Button action={() => downloadFile()}>
                    Datei herunterladen
                    <Download />
                  </Button>
                </div>
              )}
            </div>
          )}
          <div className="award-info">
            <SectionHeader
              dangerouslySetInnerHTML={parseCMSBold(
                getModuleValue(
                  data?.catalogue?.content?.modules,
                  tabContent[selectedTab]?.ident,
                  "metadata.copy",
                ) ?? "",
              )}
            />
            <div
              className="content"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(getModuleValue(
                  data?.catalogue?.content?.modules,
                  tabContent[selectedTab]?.ident,
                  "metadata.about",
                )),
              }}
            />
          </div>
          <div className="statute-point-wrapper">
            {parseHTMLToText(
              getModuleValue(modules, redeemModuleType, "metadata.howToUse"),
              "li",
              true,
            )?.length !== 0 && (
              <UnderlineButton
                className="how-to-use"
                onClick={() =>
                  NiceModal.show("howToUse-modal", {
                    howToUse: parseHTMLToText(
                      getModuleValue(
                        modules,
                        redeemModuleType,
                        "metadata.howToUse",
                      ),
                      "li",
                      true,
                    ),
                  })
                }
              >
                Anwendungstipps
              </UnderlineButton>
            )}
            {data?.catalogue?.termsUrl && (
              <UnderlineButton
                href={data?.catalogue?.termsUrl}
                target={"_blank"}
              >
                Allgemeine Bestimmungen
              </UnderlineButton>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
